/**
 * AudioRoomContext — Global state for persistent audio rooms.
 *
 * Lifts the LiveKit connection + room state out of the AudioRoom page
 * so audio continues when navigating to other pages. A mini-player
 * bar appears at the bottom when in a room but on a different page.
 */
import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { Room, RoomEvent, createLocalAudioTrack, createLocalVideoTrack, VideoPresets, Track } from 'livekit-client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../Network/API';

// Brief, consistent lock/unlock feedback used by both the host action and the
// DataChannel handler so participants always know when the room state changes.
const lockToast = (locked) =>
    toast.info(locked ? '🔒 Room locked by host' : '🔓 Room unlocked by host', {
        autoClose: 2000,
        position: 'bottom-center',
        theme: 'dark',
        hideProgressBar: true,
        style: { borderRadius: '24px', fontSize: '13px', textAlign: 'center', marginBottom: '80px', width: 'auto', display: 'inline-block' },
    });

const AudioRoomContext = createContext(null);

export const useAudioRoom = () => {
    const ctx = useContext(AudioRoomContext);
    if (!ctx) throw new Error('useAudioRoom must be used within AudioRoomProvider');
    return ctx;
};

export const AudioRoomProvider = ({ children }) => {
    // ─── Room & Connection State (persists across pages) ───
    const [activeRoom, setActiveRoom] = useState(null);
    const [currentRole, setCurrentRole] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [activeSpeakers, setActiveSpeakers] = useState(new Set());
    const [unmutedParticipants, setUnmutedParticipants] = useState(new Set());
    const [handRaised, setHandRaised] = useState(false);
    const [speakerHandRaisedIds, setSpeakerHandRaisedIds] = useState(new Set());
    const [reactions, setReactions] = useState([]);
    const [chatMessages, setChatMessages] = useState([]);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingStartedBy, setRecordingStartedBy] = useState(null);
    const [isRoomLocked, setIsRoomLocked] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [connectionQualities, setConnectionQualities] = useState({}); // { identity: quality }
    const [isBrowserOffline, setIsBrowserOffline] = useState(!navigator.onLine);
    const [isMuteLocked, setIsMuteLocked] = useState(false);
    const [muteLockMessage, setMuteLockMessage] = useState('');
    const [speakerInvite, setSpeakerInvite] = useState(null); // { fromName, userId }
    const speakerInviteTimerRef = useRef(null);
    const [lastActiveSpeakerName, setLastActiveSpeakerName] = useState(null);
    const lastSpeakerTimerRef = useRef(null);
    const [speakerRequestNotification, setSpeakerRequestNotification] = useState(null);
    const speakerRequestTimerRef = useRef(null);

    // ─── Live video stream state (inert for audio rooms — no video tracks
    // are ever published or subscribed there, so these never change) ───
    const [remoteVideoTracks, setRemoteVideoTracks] = useState({}); // { identity: RemoteVideoTrack }
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [cameraFacing, setCameraFacing] = useState('user'); // 'user' | 'environment'

    // LiveKit refs (persist across re-renders)
    const livekitRoom = useRef(null);
    const localAudioTrack = useRef(null);
    const localVideoTrack = useRef(null);
    const activeRoomIdRef = useRef(null);
    const activeRoomRef = useRef(null);
    const refreshIntervalRef = useRef(null);
    const currentRoleRef = useRef(null); // ref for use inside event closures
    const isReconnectingRef = useRef(false);

    const navigate = useNavigate();
    const currentUser = JSON.parse(localStorage.getItem('user_data') || '{}');
    if (currentUser.id) currentUser.id = parseInt(currentUser.id);

    // Keep activeRoomRef in sync with state for use inside LiveKit callbacks
    useEffect(() => { activeRoomRef.current = activeRoom; }, [activeRoom]);
    useEffect(() => { currentRoleRef.current = currentRole; }, [currentRole]);

    // Reset all live-video state (function declaration so it is hoisted and
    // callable from handlers declared above its position). No-op in audio rooms.
    function cleanupVideoState() {
        try {
            if (localVideoTrack.current) {
                if (livekitRoom.current) {
                    livekitRoom.current.localParticipant.unpublishTrack(localVideoTrack.current);
                }
                localVideoTrack.current.stop();
            }
        } catch (e) { /* best effort */ }
        localVideoTrack.current = null;
        setIsCameraOn(false);
        setRemoteVideoTracks({});
    }

    // ─── Connect to LiveKit ───
    const connectToLiveKit = useCallback(async (url, token, canPublish) => {
        try {
            // Same Room configuration as MeetingRoom.js (the proven-working path).
            // Deliberately NO rtcConfig override: an earlier LAN optimization set
            // iceServers: [] here, which also discarded the STUN/TURN servers the
            // LiveKit server provides at join — that works on a LAN (direct host
            // candidates) but breaks every connection to a remote server behind
            // NAT. Server-provided ICE handles both cases correctly.
            const room = new Room({
                adaptiveStream: true,
                dynacast: true,
                videoCaptureDefaults: {
                    resolution: VideoPresets.h720.resolution,
                },
                publishDefaults: {
                    simulcast: true,
                    dtx: true,
                },
            });

            room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
                const ids = new Set(speakers.map(s => s.identity));
                setActiveSpeakers(ids);

                // Persist last speaker name for mini player visibility
                if (speakers.length > 0) {
                    // Resolve name from active room participants
                    const speakerId = speakers[0].identity; // e.g. "123_web"
                    const numericId = speakerId?.split('_')[0];
                    const roomData = activeRoomRef.current;
                    const participant = (roomData?.participants || []).find(
                        p => String(p.user_id) === numericId
                    );
                    if (participant?.user?.name) {
                        setLastActiveSpeakerName(participant.user.name);
                    }
                    // Clear any pending timeout
                    if (lastSpeakerTimerRef.current) {
                        clearTimeout(lastSpeakerTimerRef.current);
                        lastSpeakerTimerRef.current = null;
                    }
                } else {
                    // No active speakers — start timeout to clear name after 8s
                    if (!lastSpeakerTimerRef.current) {
                        lastSpeakerTimerRef.current = setTimeout(() => {
                            setLastActiveSpeakerName(null);
                            lastSpeakerTimerRef.current = null;
                        }, 8000);
                    }
                }
            });

            // Track per-participant audio mute state (stable, no flickering)
            room.on(RoomEvent.TrackMuted, (publication, participant) => {
                if (publication.kind === 'audio') {
                    setUnmutedParticipants(prev => {
                        const next = new Set(prev);
                        next.delete(participant.identity);
                        return next;
                    });
                }
            });

            room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
                if (publication.kind === 'audio') {
                    setUnmutedParticipants(prev => new Set(prev).add(participant.identity));
                }
            });

            room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
                if (track.kind === 'audio' && !publication.isMuted) {
                    setUnmutedParticipants(prev => new Set(prev).add(participant.identity));
                }
            });

            room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
                if (track.kind === 'audio') {
                    setUnmutedParticipants(prev => {
                        const next = new Set(prev);
                        next.delete(participant.identity);
                        return next;
                    });
                }
            });

            room.on(RoomEvent.ParticipantConnected, (participant) => {
                if (!participant || !participant.identity) return;
                const userId = parseInt(participant.identity.split('_')[0]);
                const name = participant.name || 'User';

                // Fix A: Read the role the backend embedded in the JWT metadata.
                // This is the same approach X Spaces uses — the real role travels with
                // the LiveKit participant object, so we never need a REST round-trip.
                // Fall back to 'listener' only if metadata is absent (old tokens in flight).
                let metaRole = 'listener';
                let metaAvatar = null;
                try {
                    if (participant.metadata) {
                        const meta = JSON.parse(participant.metadata);
                        if (meta?.role) metaRole = meta.role;
                        if (meta?.avatar) metaAvatar = meta.avatar;
                    }
                } catch (_) { /* malformed metadata — fall back to listener */ }

                setActiveRoom(prev => {
                    if (!prev || !prev.participants) return prev;
                    // If we already have a record for this user (e.g. from the join
                    // REST payload), update their role rather than inserting a duplicate.
                    const alreadyExists = prev.participants.some(p => p.user_id === userId);
                    if (alreadyExists) {
                        // Update role from metadata in case the existing record is stale
                        return {
                            ...prev,
                            participants: prev.participants.map(p =>
                                p.user_id === userId
                                    ? { ...p, role: metaRole, user: { ...p.user, image: metaAvatar || p.user?.image } }
                                    : p
                            ),
                        };
                    }
                    const newParticipant = {
                        user_id: userId,
                        role: metaRole,  // real role from JWT metadata
                        joined_at: new Date().toISOString(),
                        user: {
                            id: userId,
                            name: name,
                            image: metaAvatar, // real avatar URL from metadata (null if not set)
                        },
                    };
                    return { ...prev, participants: [...prev.participants, newParticipant] };
                });
            });

            room.on(RoomEvent.ParticipantDisconnected, (participant) => {
                if (!participant || !participant.identity) return;
                const userId = parseInt(participant.identity.split('_')[0]);
                
                setActiveRoom(prev => {
                    if (!prev || !prev.participants) return prev;
                    return { ...prev, participants: prev.participants.filter(p => p.user_id !== userId) };
                });
            });

            room.on(RoomEvent.Disconnected, () => {
                // Don't reset isConnected during intentional promotion reconnects
                if (!isReconnectingRef.current) {
                    setIsConnected(false);
                    setIsReconnecting(false);
                }
                // Mute local mic on disconnect to stop audio capture
                try {
                    room.localParticipant?.audioTrackPublications?.forEach(pub => {
                        pub.track?.stop();
                    });
                } catch (e) { console.warn('AudioRoom: failed to stop mic on disconnect:', e); }
            });

            room.on(RoomEvent.Reconnecting, () => {
                console.log('AudioRoom: reconnecting...');
                setIsReconnecting(true);
            });

            room.on(RoomEvent.Reconnected, () => {
                console.log('AudioRoom: reconnected!');
                setIsReconnecting(false);
            });

            room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
                const identity = participant?.identity || '';
                console.log(`AudioRoom: quality changed — ${identity}: ${quality}`);
                setConnectionQualities(prev => ({ ...prev, [identity]: quality }));
            });

            // Browser-level offline/online detection (instant, supplements LiveKit quality)
            const handleOffline = () => {
                console.log('AudioRoom: browser went offline');
                setIsBrowserOffline(true);
                setIsReconnecting(true);
                // Mute mic immediately when offline
                try {
                    room.localParticipant?.audioTrackPublications?.forEach(pub => {
                        pub.mute();
                    });
                } catch (e) { console.warn('AudioRoom: failed to mute mic on offline:', e); }
            };
            const handleOnline = () => {
                console.log('AudioRoom: browser came online');
                setIsBrowserOffline(false);
                // isReconnecting will be cleared by RoomEvent.Reconnected
            };
            window.addEventListener('offline', handleOffline);
            window.addEventListener('online', handleOnline);

            // Handle incoming data messages (reactions, hand raise, etc.)
            room.on(RoomEvent.DataReceived, async (payload, participant, kind, topic) => {
                try {
                    const message = new TextDecoder().decode(payload);
                    const data = JSON.parse(message);
                    if (data.type === 'reaction' && data.emoji) {
                        const reaction = {
                            id: Date.now() + Math.random(),
                            emoji: data.emoji,
                            userId: data.user_id,
                        };
                        setReactions(prev => [...prev, reaction]);
                        setTimeout(() => {
                            setReactions(prev => prev.filter(r => r.id !== reaction.id));
                        }, 2000);
                    } else if (data.type === 'mute_all') {
                        // Host/co-host muted all — force-mute and lock (but NEVER mute the host)
                        if (currentRoleRef.current !== 'host') {
                            const lp = livekitRoom.current?.localParticipant;
                            if (lp) {
                                lp.setMicrophoneEnabled(false);
                                if (localAudioTrack.current) {
                                    localAudioTrack.current.mute();
                                }
                                setIsMuted(true);
                            }
                        }
                        setIsMuteLocked(true); // prevent speakers from unmuting (host can still override)
                    } else if (data.type === 'unmute_all') {
                        // Host unlocked muting — speakers can now unmute
                        setIsMuteLocked(false);
                    } else if (data.type === 'hand_raise') {
                        const targetId = parseInt(data.user_id);
                        const action = data.action || 'raise';
                        
                        // Optimistically update the hand_raised_at for the target user without REST call
                        setActiveRoom(prev => {
                            if (!prev || !prev.participants) return prev;
                            const updParticipants = prev.participants.map(p => 
                                p.user_id === targetId ? { ...p, hand_raised_at: action === 'raise' ? new Date().toISOString() : null } : p
                            );
                            return { ...prev, participants: updParticipants };
                        });

                        if (action === 'raise') {
                            const curRole = prevRoleRef.current;
                            if (['host', 'co_host'].includes(curRole)) {
                                const reqName = data.user_name || 'Someone';
                                setSpeakerRequestNotification({ name: reqName, id: Date.now() });
                                if (speakerRequestTimerRef.current) clearTimeout(speakerRequestTimerRef.current);
                                speakerRequestTimerRef.current = setTimeout(() => {
                                    setSpeakerRequestNotification(null);
                                    speakerRequestTimerRef.current = null;
                                }, 4000);
                            }
                        }
                    } else if (data.type === 'hand_lower') {
                        const targetId = parseInt(data.user_id);
                        setActiveRoom(prev => {
                            if (!prev || !prev.participants) return prev;
                            const updParticipants = prev.participants.map(p => 
                                p.user_id === targetId ? { ...p, hand_raised_at: null } : p
                            );
                            return { ...prev, participants: updParticipants };
                        });
                    } else if (data.type === 'room_update') {
                        // Title changed on the fly
                        setActiveRoom(prev => {
                            if (!prev) return prev;
                            return { ...prev, title: data.title };
                        });
                    } else if (data.type === 'speaker_hand_raise') {
                        // Speaker visual-only hand raise
                        const userId = parseInt(data.user_id);
                        if (userId) {
                            setSpeakerHandRaisedIds(prev => new Set([...prev, userId]));
                        }
                    } else if (data.type === 'speaker_hand_lower') {
                        // Speaker visual-only hand lower
                        const userId = parseInt(data.user_id);
                        if (userId) {
                            setSpeakerHandRaisedIds(prev => {
                                const next = new Set(prev);
                                next.delete(userId);
                                return next;
                            });
                        }
                    } else if (data.type === 'recording_started') {
                        // GDPR: notify all participants that recording has begun
                        setIsRecording(true);
                        setRecordingStartedBy(data.user_name || 'Host');
                    } else if (data.type === 'recording_stopped') {
                        setIsRecording(false);
                        setRecordingStartedBy(null);
                    } else if (data.type === 'speaker_invite') {
                        // Check if invite is for us
                        const targetId = parseInt(data.user_id);
                        if (targetId === currentUser?.id) {
                            setSpeakerInvite({
                                fromName: data.from_user_name || 'Host',
                                userId: data.from_user_id,
                            });
                            // Auto-dismiss after 15s
                            if (speakerInviteTimerRef.current) clearTimeout(speakerInviteTimerRef.current);
                            speakerInviteTimerRef.current = setTimeout(() => {
                                setSpeakerInvite(null);
                            }, 15000);
                        }
                    } else if (data.type === 'chat_message' && data.data) {
                        setChatMessages(prev => {
                            if (prev.some(m => m.id === data.data.id)) return prev;
                            return [...prev, {
                                id: data.data.id,
                                user_id: data.data.user_id,
                                message: data.data.message,
                                created_at: data.data.created_at,
                                user: {
                                    id: data.data.user_id,
                                    name: data.data.user_name,
                                    image: data.data.user_image,
                                }
                            }];
                        });
                    } else if (data.type === 'room_ended') {
                        // Host ended the room — exit cleanly before LiveKit fires disconnect
                        console.log('AudioRoom: room_ended received, cleaning up.');
                        const wasVideo = activeRoomRef.current?.room_type === 'video';
                        stopPeriodicRefresh();
                        stopTokenRefresh();
                        cleanupVideoState();
                        if (livekitRoom.current) {
                            livekitRoom.current.disconnect();
                            livekitRoom.current = null;
                        }
                        setActiveRoom(null);
                        setCurrentRole(null);
                        setIsConnected(false);
                        activeRoomIdRef.current = null;
                        navigate(wasVideo ? '/live-stream' : '/audio-rooms');
                    } else if (data.type === 'kicked') {
                        // Check if we were kicked
                        const kickedId = parseInt(data.user_id);
                        if (kickedId === currentUser?.id) {
                            console.log('AudioRoom: we were kicked');
                            const wasVideo = activeRoomRef.current?.room_type === 'video';
                            stopPeriodicRefresh();
                            stopTokenRefresh();
                            cleanupVideoState();
                            if (livekitRoom.current) {
                                livekitRoom.current.disconnect();
                                livekitRoom.current = null;
                            }
                            setActiveRoom(null);
                            setCurrentRole(null);
                            setIsConnected(false);
                            activeRoomIdRef.current = null;
                            alert('You have been removed from the room.');
                            navigate(wasVideo ? '/live-stream' : '/audio-rooms');
                        }
                    } else if (data.type === 'role_change') {
                        const targetId = parseInt(data.user_id);
                        const newRole = data.new_role;

                        // 1. Update UI instantly for EVERYONE (self and observers) bypassing REST latency.
                        //    Track whether the target is already in our roster — if not, they likely
                        //    rejoined after a transient drop (ParticipantDisconnected filtered them out)
                        //    and we must pull the authoritative roster from REST so they reappear with
                        //    the correct role instead of only after the 60s poll. This is the parity
                        //    counterpart to the app's not-found refresh; without it a reconnected host
                        //    stays invisible to participants here.
                        let targetInRoster = false;
                        setActiveRoom(prev => {
                            if (!prev || !prev.participants) return prev;
                            targetInRoster = prev.participants.some(p => parseInt(p.user_id) === targetId);
                            const updParticipants = prev.participants.map(p =>
                                parseInt(p.user_id) === targetId ? { ...p, role: newRole, hand_raised_at: null } : p
                            );
                            return { ...prev, participants: updParticipants };
                        });

                        if (!targetInRoster && targetId !== currentUser?.id) {
                            refreshRoomDetails();
                        }

                        // 2. If it's US being promoted/demoted, handle the deep state constraints instantly
                        if (targetId === currentUser?.id) {
                            const isSpeakerRole = ['host', 'co_host', 'speaker'].includes(newRole);
                            const wasSpeaker = ['host', 'co_host', 'speaker'].includes(prevRoleRef.current);
                            
                            prevRoleRef.current = newRole;
                            setCurrentRole(newRole);
                            if (isSpeakerRole) setHandRaised(false);

                            // Demoted off the stage in a video room: stop our camera
                            // (remote tracks stay — we keep watching as a viewer).
                            if (wasSpeaker && !isSpeakerRole && localVideoTrack.current) {
                                try {
                                    livekitRoom.current?.localParticipant?.unpublishTrack(localVideoTrack.current);
                                    localVideoTrack.current.stop();
                                } catch (err) { /* best effort */ }
                                localVideoTrack.current = null;
                                setIsCameraOn(false);
                            }

                            // Detect promotion: publish native track implicitly
                            if (!wasSpeaker && isSpeakerRole && !localAudioTrack.current && livekitRoom.current) {
                                console.log(`AudioRoom: Instantly promoted to ${newRole}! Booting audio driver natively...`);
                                try {
                                    const track = await createLocalAudioTrack({
                                        noiseSuppression: true,
                                        echoCancellation: true,
                                        autoGainControl: true,
                                    });
                                    await livekitRoom.current.localParticipant.publishTrack(track);
                                    localAudioTrack.current = track;
                                    await track.mute();
                                    await livekitRoom.current.localParticipant.setMicrophoneEnabled(false);
                                    setIsMuted(true);
                                    // Re-apply mute lock: if host activated mute-all before
                                    // this promotion, lock the newly promoted speaker.
                                    if (activeRoomRef.current?.is_mute_locked && newRole !== 'host') {
                                        setIsMuteLocked(true);
                                    }
                                } catch (err) {
                                    console.error('Instant promotion audio publish failed:', err);
                                }
                            }
                            
                            // Let the background silently sync cache without disrupting UX
                            refreshRoomDetails();
                        }
                    } else if (data.type === 'room_lock') {
                        // Room lock/unlock — update lock indicator + notify participants.
                        // The host (sender) doesn't receive its own DataChannel message,
                        // so this toast only fires for other participants.
                        const nowLocked = !!data.is_locked;
                        setIsRoomLocked(prev => {
                            if (prev !== nowLocked) lockToast(nowLocked);
                            return nowLocked;
                        });
                    } else if (data.type === 'open_mic_toggled') {
                        setActiveRoom(prev => {
                            if (!prev) return prev;
                            return { ...prev, is_open_mic: !!data.is_open_mic };
                        });
                    }
                } catch (e) {
                    console.error('Data message parse error:', e);
                }
            });

            room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
                if (track.kind === 'audio') {
                    const audioEl = track.attach();
                    audioEl.id = `audio-${participant.identity}`;
                    document.body.appendChild(audioEl);
                } else if (track.kind === 'video') {
                    // Live video streams: keep the track so the stage grid can attach it.
                    setRemoteVideoTracks(prev => ({ ...prev, [participant.identity]: track }));
                }
            });

            room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
                track.detach().forEach(el => el.remove());
                const el = document.getElementById(`audio-${participant.identity}`);
                if (el) el.remove();
                if (track.kind === 'video') {
                    setRemoteVideoTracks(prev => {
                        const next = { ...prev };
                        delete next[participant.identity];
                        return next;
                    });
                }
            });

            // A remote stage member toggling their camera mutes/unmutes the video
            // publication rather than unpublishing — drop/restore the tile accordingly.
            room.on(RoomEvent.TrackMuted, (publication, participant) => {
                if (publication.kind === 'video') {
                    setRemoteVideoTracks(prev => {
                        const next = { ...prev };
                        delete next[participant.identity];
                        return next;
                    });
                }
            });
            room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
                if (publication.kind === 'video' && publication.track) {
                    setRemoteVideoTracks(prev => ({ ...prev, [participant.identity]: publication.track }));
                }
            });

            await room.connect(url, token);
            livekitRoom.current = room;

            if (canPublish) {
                // Mic capture can fail independently of the room connection —
                // permission denied, no device, or an insecure (http) origin where
                // navigator.mediaDevices doesn't exist. Stay connected as a muted
                // speaker instead of aborting the whole session.
                try {
                    const track = await createLocalAudioTrack({
                        noiseSuppression: true,   // Real-time noise suppression (NS)
                        echoCancellation: true,   // Acoustic echo cancellation (AEC)
                        autoGainControl: true,    // Automatic gain control (AGC)
                    });
                    await room.localParticipant.publishTrack(track);
                    localAudioTrack.current = track;
                    // Start muted — speaker must tap Unmute when ready
                    await track.mute();
                    await room.localParticipant.setMicrophoneEnabled(false);
                    setIsMuted(true);
                } catch (micErr) {
                    console.error('Mic capture failed (continuing without publishing):', micErr);
                    setIsMuted(true);
                    toast.error(
                        window.isSecureContext === false
                            ? 'Mic/camera need HTTPS or localhost — you are connected as listen-only.'
                            : 'Microphone unavailable — you are connected as listen-only.',
                        { autoClose: 5000 }
                    );
                }
            }

            setIsConnected(true);
            startPeriodicRefresh();
            startTokenRefresh();
            return true;
        } catch (e) {
            console.error('LiveKit connect failed:', e);
            return false;
        }
    }, []);

    // ─── Periodic Refresh ───
    const startPeriodicRefresh = useCallback(() => {
        if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = setInterval(() => {
            refreshRoomDetails();
        }, 60000); // 60s safety-net sync (real-time via data channel)
    }, []);

    const stopPeriodicRefresh = useCallback(() => {
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }
    }, []);

    // ─── Token Refresh ───
    const tokenRefreshIntervalRef = useRef(null);

    const startTokenRefresh = useCallback(() => {
        if (tokenRefreshIntervalRef.current) clearInterval(tokenRefreshIntervalRef.current);
        tokenRefreshIntervalRef.current = setInterval(async () => {
            const roomId = activeRoomIdRef.current;
            if (!roomId || !livekitRoom.current) return;
            try {
                const res = await API.post(`/v1/audio-rooms/${roomId}/new-token`);
                if (res.data?.success && res.data.data?.token) {
                    // Update connection token silently
                    livekitRoom.current?.engine?.client?.session?.updateToken?.(res.data.data.token);
                    if (typeof livekitRoom.current?.updateToken === 'function') {
                        livekitRoom.current.updateToken(res.data.data.token);
                    }
                }
            } catch (e) {
                console.error('Silent token refresh failed:', e);
            }
        }, 720000); // 12 minutes
    }, []);

    const stopTokenRefresh = useCallback(() => {
        if (tokenRefreshIntervalRef.current) {
            clearInterval(tokenRefreshIntervalRef.current);
            tokenRefreshIntervalRef.current = null;
        }
    }, []);

    // ─── Refresh Room Details ───
    const prevRoleRef = useRef(null);

    const refreshRoomDetails = useCallback(async () => {
        const roomId = activeRoomIdRef.current;
        if (!roomId) return;
        try {
            const res = await API.get(`/v1/audio-rooms/${roomId}?t=${Date.now()}`);
            if (res.data?.success) {
                const room = res.data.data;

                // Fix C: Merge instead of full-replace so DataChannel-driven state
                // (is_open_mic, is_locked) set by real-time push is not clobbered by
                // the 60s polling cycle.
                // REST is authoritative for: participants list, title, egress_id, status.
                // DataChannel is authoritative for: is_open_mic, is_locked (toggled live).
                setActiveRoom(prev => ({
                    ...room,                                       // REST wins for most fields
                    is_open_mic: prev?.is_open_mic ?? !!room.is_open_mic, // preserve DC state
                    is_locked:   prev?.is_locked   ?? !!room.is_locked,   // preserve DC state
                }));

                // Sync recording state from backend (reliable — covers join + rejoin)
                setIsRecording(!!room.egress_id);

                // Sync lock state from backend
                setIsRoomLocked(!!room.is_locked);

                // Find our own participant to sync role
                const currentUser = JSON.parse(localStorage.getItem('user_data') || '{}');
                if (currentUser.id) currentUser.id = parseInt(currentUser.id);
                const myParticipant = room.participants?.find(p => parseInt(p.user_id) === currentUser.id);
                if (myParticipant) {
                    const newRole = myParticipant.role;
                    const isSpeakerRole = ['host', 'co_host', 'speaker'].includes(newRole);
                    const wasSpeaker = ['host', 'co_host', 'speaker'].includes(prevRoleRef.current);

                    // Always sync role FIRST (before any async work)
                    if (prevRoleRef.current !== newRole) {
                        console.log(`AudioRoom: role updated from ${prevRoleRef.current} to ${newRole}`);
                    }
                    prevRoleRef.current = newRole;
                    setCurrentRole(newRole);
                    // Sync handRaised from backend — only for listeners (speakers use visual-only toggle)
                    if (!isSpeakerRole) {
                        setHandRaised(!!myParticipant.hand_raised_at);
                    }

                    // Detect promotion: was listener, now speaker — publish natively instead of reconnecting
                    if (!wasSpeaker && isSpeakerRole && !localAudioTrack.current && livekitRoom.current) {
                        console.log(`AudioRoom: promoted to ${newRole}! Publishing audio track natively...`);
                        try {
                            const track = await createLocalAudioTrack({
                                noiseSuppression: true,
                                echoCancellation: true,
                                autoGainControl: true,
                            });
                            await livekitRoom.current.localParticipant.publishTrack(track);
                            localAudioTrack.current = track;
                            await track.mute();
                            await livekitRoom.current.localParticipant.setMicrophoneEnabled(false);
                            setIsMuted(true);
                            // Re-apply mute lock for the newly promoted speaker
                            if (room.is_mute_locked && newRole !== 'host') {
                                setIsMuteLocked(true);
                            }
                        } catch (err) {
                            console.error('Promotion native publish failed:', err);
                        }
                    }
                }
            }
        } catch (e) {
            console.error('Refresh room details failed:', e);
        }
    }, [connectToLiveKit]);

    // ─── Create Room ───
    // options: { roomType: 'audio'|'video', maxSpeakers: 2–8, notifyContacts: bool }
    // Defaults reproduce the pre-video behavior exactly (audio room, notify ON).
    const handleCreateRoom = useCallback(async (title, description, category, scheduledAt = null, recordEnabled = false, options = {}) => {
        try {
            const { roomType = 'audio', maxSpeakers = null, notifyContacts = true } = options;
            const body = {
                title: title.trim(),
                description: description?.trim() || undefined,
                category: category || 'General',
                notify_contacts: notifyContacts ? 1 : 0,
            };
            if (roomType === 'video') {
                body.room_type = 'video';
                if (maxSpeakers) body.max_speakers = maxSpeakers;
            }
            if (scheduledAt) body.scheduled_at = scheduledAt;

            const res = await API.post('/v1/audio-rooms', body);

            if (res.data?.success) {
                const { room, token, livekit_url, timeline_post, session_key } = res.data.data;
                if (session_key) localStorage.setItem('audio_room_session_key', session_key);

                // Guard: if we asked for a video stream but the backend created an
                // audio room, the server is running pre-video-streaming code (or
                // its migration hasn't run). Clean up the stray room and fail
                // loudly instead of silently landing in an audio room.
                if (roomType === 'video' && room?.room_type !== 'video') {
                    try { await API.post(`/v1/audio-rooms/${room.id}/end`); } catch (err) { /* best effort */ }
                    toast.error('Live video is not enabled on the server yet — the backend needs to be updated.', { autoClose: 5000 });
                    return false;
                }

                // Sync the local Redux timeline with the new Post immediately
                if (timeline_post) {
                    try {
                        const store = require('../store').default;
                        const { ADD_POST_FETCH_SUCCESS } = require('../Reducer/postReducer');
                        store.dispatch(ADD_POST_FETCH_SUCCESS(timeline_post));
                    } catch (err) {
                        console.error('Instant Redux timeline sync failed:', err);
                    }
                }

                // Scheduled room — no LiveKit connection
                if (scheduledAt) {
                    return { scheduled: true, room };
                }

                setActiveRoom(room);
                setCurrentRole('host');
                prevRoleRef.current = 'host';
                activeRoomIdRef.current = room.id;

                const hostConnected = await connectToLiveKit(livekit_url, token, true);
                if (!hostConnected) {
                    // Room row exists but the media server is unreachable — end the
                    // stray room and fail loudly rather than stranding the host.
                    try { await API.post(`/v1/audio-rooms/${room.id}/end`); } catch (err) { /* best effort */ }
                    toast.error('Could not connect to the media server — check that LiveKit is reachable.', { autoClose: 5000 });
                    return false;
                }

                // Live video stream host: go live with camera + mic on immediately.
                if (room.room_type === 'video') {
                    try {
                        const vtrack = await createLocalVideoTrack({
                            resolution: VideoPresets.h720.resolution,
                            facingMode: 'user',
                        });
                        await livekitRoom.current.localParticipant.publishTrack(vtrack, {
                            source: Track.Source.Camera,
                            simulcast: true,
                        });
                        localVideoTrack.current = vtrack;
                        setIsCameraOn(true);
                    } catch (e) {
                        console.error('Host camera start failed:', e);
                        toast.error('Camera unavailable — streaming audio only.', { autoClose: 3000 });
                    }
                    try {
                        if (localAudioTrack.current) {
                            await localAudioTrack.current.unmute();
                            setIsMuted(false);
                        }
                    } catch (e) { console.error('Host mic unmute failed:', e); }
                }

                // Immediately auto-start recording if requested
                if (recordEnabled && room.id) {
                    try {
                        await API.post(`/v1/audio-rooms/${room.id}/recording/start`);
                        setIsRecording(true);
                    } catch (e) {
                        console.error('Auto-start recording failed', e);
                    }
                }

                return true;
            }
        } catch (e) {
            console.error('Create room failed:', e);
        }
        return false;
    }, [connectToLiveKit]);

    // ─── Join Room ───
    // preferredRole: 'listener' | 'speaker' — only used when room.is_open_mic === true
    const handleJoinRoom = useCallback(async (roomId, preferredRole = 'listener') => {
        try {
            // Append role query param so backend knows the preferred entry mode.
            // For non-open-mic rooms the backend ignores it and defaults to 'listener'.
            const url = preferredRole === 'speaker'
                ? `/v1/audio-rooms/${roomId}/join?role=speaker`
                : `/v1/audio-rooms/${roomId}/join`;
            const res = await API.post(url);

            if (res.data?.success === false) {
                // Surface the backend's reason (room ended, locked, kicked…)
                toast.error(res.data?.message || 'Could not join this room.', { autoClose: 4000 });
            }

            if (res.data?.success) {
                const { room, token, livekit_url, role, session_key } = res.data.data;
                if (session_key) localStorage.setItem('audio_room_session_key', session_key);
                setActiveRoom(room);
                setCurrentRole(role || 'listener');
                prevRoleRef.current = role || 'listener';
                activeRoomIdRef.current = room.id;
                
                // Immediately consume recording state metadata instead of waiting for periodic refresh tick
                setIsRecording(!!room.egress_id);

                const canPublish = ['host', 'co_host', 'speaker'].includes(role);
                // Surface LiveKit connection failures to callers — previously this
                // returned true even when the media connection never came up,
                // leaving pages waiting on isConnected with no error shown.
                const connected = await connectToLiveKit(livekit_url, token, canPublish);
                return connected === true;
            }
        } catch (e) {
            console.error('Join room failed:', e);
        }
        return false;
    }, [connectToLiveKit]);

    // ─── Leave Room ───
    const handleLeaveRoom = useCallback(async () => {
        if (!activeRoom) return;

        stopPeriodicRefresh();
        stopTokenRefresh();
        cleanupVideoState();

        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/leave`);
        } catch (e) {
            console.error('Leave API failed:', e);
        }

        if (livekitRoom.current) {
            livekitRoom.current.disconnect();
            livekitRoom.current = null;
        }
        localAudioTrack.current = null;

        setActiveRoom(null);
        setCurrentRole(null);
        prevRoleRef.current = null;
        setIsConnected(false);
        setIsMuted(false);
        setHandRaised(false);
        setActiveSpeakers(new Set());
        activeRoomIdRef.current = null;
    }, [activeRoom, stopPeriodicRefresh, stopTokenRefresh]);

    // ─── End Room ───
    const handleEndRoom = useCallback(async () => {
        if (!activeRoom) return;

        stopPeriodicRefresh();
        stopTokenRefresh();
        cleanupVideoState();

        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/end`);
        } catch (e) {
            console.error('End room failed:', e);
        }

        if (livekitRoom.current) {
            livekitRoom.current.disconnect();
            livekitRoom.current = null;
        }

        setActiveRoom(null);
        setCurrentRole(null);
        prevRoleRef.current = null;
        setIsConnected(false);
        setIsMuted(false);
        setActiveSpeakers(new Set());
        activeRoomIdRef.current = null;
    }, [activeRoom, stopPeriodicRefresh, stopTokenRefresh]);

    // ─── Toggle Mute ───
    const toggleMute = useCallback(async () => {
        if (localAudioTrack.current) {
            const myIdentity = livekitRoom.current?.localParticipant?.identity;
            if (isMuted) {
                // Trying to unmute — check if host has locked muting
                if (isMuteLocked && currentRole !== 'host') {
                    setMuteLockMessage('🔇 Host muted all — wait for host to unmute');
                    setTimeout(() => setMuteLockMessage(''), 2000);
                    return;
                }
                localAudioTrack.current.unmute();
                setIsMuted(false);
                if (myIdentity) setUnmutedParticipants(prev => new Set(prev).add(myIdentity));
                // Auto-lower hand when unmuting (speaker is now talking)
                if (handRaised) {
                    setHandRaised(false);
                    try {
                        const roomId = activeRoomIdRef.current;
                        if (roomId) API.post(`/v1/audio-rooms/${roomId}/lower-hand`);
                    } catch (e) { /* ignore */ }
                }
            } else {
                localAudioTrack.current.mute();
                setIsMuted(true);
                if (myIdentity) setUnmutedParticipants(prev => {
                    const next = new Set(prev);
                    next.delete(myIdentity);
                    return next;
                });
            }
        }
    }, [isMuted, isMuteLocked, handRaised]);

    // ─── Live stream: Toggle Camera (on-stage roles in video rooms only) ───
    const toggleCamera = useCallback(async () => {
        if (!livekitRoom.current) return;
        try {
            if (isCameraOn && localVideoTrack.current) {
                await livekitRoom.current.localParticipant.unpublishTrack(localVideoTrack.current);
                localVideoTrack.current.stop();
                localVideoTrack.current = null;
                setIsCameraOn(false);
            } else {
                const track = await createLocalVideoTrack({
                    resolution: VideoPresets.h720.resolution,
                    facingMode: cameraFacing,
                });
                await livekitRoom.current.localParticipant.publishTrack(track, {
                    source: Track.Source.Camera,
                    simulcast: true,
                });
                localVideoTrack.current = track;
                setIsCameraOn(true);
            }
        } catch (e) {
            console.error('Toggle camera failed:', e);
            toast.error('Camera unavailable — check browser permissions.', { autoClose: 2500 });
        }
    }, [isCameraOn, cameraFacing]);

    // ─── Live stream: Switch Camera (front/back on mobile browsers) ───
    const switchCamera = useCallback(async () => {
        const newFacing = cameraFacing === 'user' ? 'environment' : 'user';
        setCameraFacing(newFacing);
        if (!isCameraOn || !localVideoTrack.current) return;
        try {
            await localVideoTrack.current.restartTrack({ facingMode: newFacing });
        } catch (e) {
            console.error('Switch camera failed:', e);
        }
    }, [cameraFacing, isCameraOn]);

    // Stop + unpublish the local camera without touching audio state.
    const stopLocalVideo = useCallback(() => {
        try {
            if (localVideoTrack.current) {
                if (livekitRoom.current) {
                    livekitRoom.current.localParticipant.unpublishTrack(localVideoTrack.current);
                }
                localVideoTrack.current.stop();
            }
        } catch (e) { /* best effort */ }
        localVideoTrack.current = null;
        setIsCameraOn(false);
    }, []);

    // ─── Request to Speak ───
    const requestToSpeak = useCallback(async () => {
        if (!activeRoom) return;
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/request-speak`);
            setHandRaised(true);
            // Send instant data channel notification so host sees it immediately
            if (livekitRoom.current) {
                const msg = new TextEncoder().encode(
                    JSON.stringify({ type: 'hand_raise', user_id: currentUser?.id, user_name: currentUser?.name || '' })
                );
                livekitRoom.current.localParticipant.publishData(msg, { reliable: true });
            }
        } catch (e) {
            console.error('Request speak failed:', e);
        }
    }, [activeRoom]);

    // ─── Lower Hand ───
    const lowerHand = useCallback(async () => {
        if (!activeRoom) return;
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/lower-hand`);
            setHandRaised(false);
            // Broadcast hand_lower so host sees the cancel instantly
            if (livekitRoom.current) {
                const msg = new TextEncoder().encode(
                    JSON.stringify({ type: 'hand_lower', user_id: currentUser?.id })
                );
                livekitRoom.current.localParticipant.publishData(msg, { reliable: true });
            }
        } catch (e) {
            console.error('Lower hand failed:', e);
        }
    }, [activeRoom]);

    // ─── Toggle Speaker Hand (visual-only, NO API call) ───
    const toggleSpeakerHand = useCallback(() => {
        setHandRaised(prev => {
            const newVal = !prev;
            // Track locally so our own avatar shows the hand
            setSpeakerHandRaisedIds(prevIds => {
                const next = new Set(prevIds);
                if (newVal) next.add(currentUser?.id);
                else next.delete(currentUser?.id);
                return next;
            });
            // Broadcast visual signal via data channel
            if (livekitRoom.current) {
                const msg = new TextEncoder().encode(
                    JSON.stringify({
                        type: newVal ? 'speaker_hand_raise' : 'speaker_hand_lower',
                        user_id: currentUser?.id,
                        user_name: currentUser?.name || '',
                    })
                );
                livekitRoom.current.localParticipant.publishData(msg, { reliable: true });
            }
            return newVal;
        });
    }, [currentUser]);

    // ─── Helper checks ───
    const isSpeaker = ['host', 'co_host', 'speaker'].includes(currentRole);
    const canManage = ['host', 'co_host'].includes(currentRole);

    // ─── Send Reaction ───
    const sendReaction = useCallback((emoji) => {
        if (!livekitRoom.current) return;
        const message = JSON.stringify({
            type: 'reaction',
            emoji,
            user_id: currentUser?.id,
            user_name: currentUser?.name || '',
        });
        const data = new TextEncoder().encode(message);
        livekitRoom.current.localParticipant.publishData(data, { reliable: false });
        // Show locally on own avatar
        const reaction = { id: Date.now() + Math.random(), emoji, userId: currentUser?.id };
        setReactions(prev => [...prev, reaction]);
        setTimeout(() => {
            setReactions(prev => prev.filter(r => r.id !== reaction.id));
        }, 2000);
    }, [currentUser]);

    // ─── Host: Mute All ───
    const muteAll = useCallback(async () => {
        if (!activeRoom) return;
        try {
            // Server toggles is_mute_locked and broadcasts mute_all / unmute_all
            // to ALL participants (including this client) via LiveKit server-side sendData.
            // The existing DataChannel handler (type='mute_all'/'unmute_all') updates local state.
            await API.post(`/v1/audio-rooms/${activeRoom.id}/mute-all`);
        } catch (e) {
            console.error('Mute all failed:', e);
            alert('Failed to mute all — ' + (e.response?.data?.message || e.message));
        }
    }, [activeRoom]);

    // ─── Host: Toggle Open Mic Mode ───
    // Only touches is_open_mic on activeRoom — zero impact on existing audio tracks or roles.
    const toggleOpenMic = useCallback(async () => {
        if (!activeRoom) return;
        try {
            const res = await API.patch(`/v1/audio-rooms/${activeRoom.id}/open-mic`);
            if (res.data?.success) {
                const newVal = res.data.data.is_open_mic;
                setActiveRoom(prev => {
                    if (!prev) return prev;
                    return { ...prev, is_open_mic: newVal };
                });
            }
        } catch (e) {
            console.error('Toggle open mic failed:', e);
        }
    }, [activeRoom]);

    // ─── Host: Toggle Lock ───
    const toggleLock = useCallback(async () => {
        if (!activeRoom) return;
        try {
            const res = await API.post(`/v1/audio-rooms/${activeRoom.id}/lock`);
            if (res.data?.success) {
                const locked = res.data.data.is_locked;
                setIsRoomLocked(locked);
                lockToast(locked); // host confirmation (host won't receive its own broadcast)
                // Broadcast lock state so all participants see it instantly
                if (livekitRoom.current) {
                    const msg = new TextEncoder().encode(
                        JSON.stringify({ type: 'room_lock', is_locked: locked })
                    );
                    livekitRoom.current.localParticipant.publishData(msg, { reliable: true });
                }
            }
        } catch (e) {
            console.error('Toggle lock failed:', e);
        }
    }, [activeRoom]);

    // ─── Host: Start Recording ───
    const startRecording = useCallback(async () => {
        if (!activeRoom) return;
        try {
            const res = await API.post(`/v1/audio-rooms/${activeRoom.id}/recording/start`);
            if (res.data?.success) {
                setIsRecording(true);
                setRecordingStartedBy(currentUser?.name || 'Host');
                // Broadcast to all participants (GDPR notification)
                if (livekitRoom.current) {
                    const msg = new TextEncoder().encode(
                        JSON.stringify({
                            type: 'recording_started',
                            user_id: currentUser?.id,
                            user_name: currentUser?.name || 'Host',
                        })
                    );
                    livekitRoom.current.localParticipant.publishData(msg, { reliable: true });
                }
            }
        } catch (e) {
            console.error('Start recording failed:', e);
            alert('Recording failed — the recording service may not be available.');
        }
    }, [activeRoom, currentUser]);

    // ─── Host: Stop Recording ───
    const stopRecording = useCallback(async () => {
        if (!activeRoom) return;
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/recording/stop`);
            setIsRecording(false);
            setRecordingStartedBy(null);
            // Broadcast to all participants
            if (livekitRoom.current) {
                const msg = new TextEncoder().encode(
                    JSON.stringify({ type: 'recording_stopped' })
                );
                livekitRoom.current.localParticipant.publishData(msg, { reliable: true });
            }
        } catch (e) {
            console.error('Stop recording failed:', e);
            alert('Failed to stop recording — ' + (e.response?.data?.message || e.message));
        }
    }, [activeRoom]);

    // ─── Broadcast role change to all participants via data channel ───
    const broadcastRoleChange = useCallback(() => {
        if (livekitRoom.current) {
            try {
                const msg = new TextEncoder().encode(JSON.stringify({ type: 'role_change' }));
                livekitRoom.current.localParticipant.publishData(msg, { reliable: true });
            } catch (e) { console.warn('AudioRoom: failed to broadcast role_change:', e); }
        }
    }, []);

    // ─── Speaker: Step Down to Listener ───
    const stepDown = useCallback(async () => {
        if (!activeRoom || !currentUser?.id) return;
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/demote/${currentUser.id}`);
            setCurrentRole('listener');
            prevRoleRef.current = 'listener';
            setHandRaised(false);

            // Clean up audio track
            if (localAudioTrack.current) {
                localAudioTrack.current.stop();
                localAudioTrack.current = null;
            }

            // Video room: stop our camera when stepping off the stage
            if (localVideoTrack.current) {
                try {
                    livekitRoom.current?.localParticipant?.unpublishTrack(localVideoTrack.current);
                    localVideoTrack.current.stop();
                } catch (err) { /* best effort */ }
                localVideoTrack.current = null;
                setIsCameraOn(false);
            }

            broadcastRoleChange();
        } catch (e) {
            isReconnectingRef.current = false;
            console.error('Step down failed:', e);
        }
    }, [activeRoom, currentUser, connectToLiveKit]);

    // ─── Invite to Speak (host/co-host) ───
    const inviteToSpeak = useCallback(async (userId) => {
        if (!activeRoom) return;
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/invite-speak/${userId}`);
            // Send data channel message for instant notification
            if (livekitRoom.current?.localParticipant) {
                const msg = JSON.stringify({
                    type: 'speaker_invite',
                    user_id: userId,
                    from_user_id: currentUser?.id,
                    from_user_name: currentUser?.name || 'Host',
                });
                const encoder = new TextEncoder();
                livekitRoom.current.localParticipant.publishData(
                    encoder.encode(msg),
                    { reliable: true }
                );
            }
        } catch (e) {
            console.error('Invite to speak failed:', e);
        }
    }, [activeRoom, currentUser]);

    // ─── Accept Speaker Invite ───
    const acceptSpeakInvite = useCallback(async () => {
        if (!activeRoom) return;
        setSpeakerInvite(null);
        if (speakerInviteTimerRef.current) clearTimeout(speakerInviteTimerRef.current);
        try {
            const res = await API.post(`/v1/audio-rooms/${activeRoom.id}/accept-invite`);
            if (res.data?.success) {
                setCurrentRole('speaker');
                prevRoleRef.current = 'speaker';
                refreshRoomDetails();
                broadcastRoleChange();
            }
        } catch (e) {
            isReconnectingRef.current = false;
            console.error('Accept invite failed:', e);
        }
    }, [activeRoom, connectToLiveKit]);

    // ─── Reject Speaker Invite ───
    const rejectSpeakInvite = useCallback(async () => {
        if (!activeRoom) return;
        setSpeakerInvite(null);
        if (speakerInviteTimerRef.current) clearTimeout(speakerInviteTimerRef.current);
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/reject-invite`);
        } catch (e) {
            console.error('Reject invite failed:', e);
        }
    }, [activeRoom]);

    // ─── Revoke Co-Host (host only) ───
    const revokeCoHost = useCallback(async (userId) => {
        if (!activeRoom) return;
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/revoke-cohost/${userId}`);
            refreshRoomDetails();
            broadcastRoleChange();
        } catch (e) {
            console.error('Revoke co-host failed:', e);
        }
    }, [activeRoom]);

    // ─── Edit Room Title (Host/Co-host only) ───
    const editRoomTitle = useCallback(async (newTitle) => {
        if (!activeRoom) return;
        try {
            await API.put(`/v1/audio-rooms/${activeRoom.id}`, { title: newTitle });
            setActiveRoom(prev => {
                if (!prev) return prev;
                return { ...prev, title: newTitle };
            });
        } catch (e) {
            console.error('Edit room title failed:', e);
        }
    }, [activeRoom]);

    // ─── Clean up on unmount (app closing) ───
    useEffect(() => {
        return () => {
            stopPeriodicRefresh();
            stopTokenRefresh();
            if (livekitRoom.current) {
                livekitRoom.current.disconnect();
            }
        };
    }, [stopPeriodicRefresh, stopTokenRefresh]);

    // ─── Tab/window close: notify backend to mark participant as left ───
    useEffect(() => {
        const handleBeforeUnload = () => {
            const roomId = activeRoomIdRef.current;
            if (!roomId) return;
            // fetch with keepalive survives page unload and supports auth headers
            const token = localStorage.getItem('connect_token');
            const baseUrl = process.env.REACT_APP_API_URL || '';
            fetch(`${baseUrl}/v1/audio-rooms/${roomId}/leave`, {
                method: 'POST',
                keepalive: true,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            }).catch(() => { }); // fire-and-forget
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    const value = {
        // State
        activeRoom,
        setActiveRoom,
        currentRole,
        setCurrentRole,
        isConnected,
        isMuted,
        activeSpeakers,
        lastActiveSpeakerName,
        speakerRequestNotification,
        dismissSpeakerRequest: () => {
            setSpeakerRequestNotification(null);
            if (speakerRequestTimerRef.current) clearTimeout(speakerRequestTimerRef.current);
        },
        unmutedParticipants,
        handRaised,
        speakerHandRaisedIds,
        setHandRaised,
        currentUser,
        isSpeaker,
        canManage,
        reactions,
        chatMessages,
        setChatMessages,
        isRecording,
        recordingStartedBy,
        isReconnecting,
        connectionQualities,
        isBrowserOffline,
        isRoomLocked,
        isMuteLocked,
        muteLockMessage,

        // Live video stream
        remoteVideoTracks,
        isCameraOn,
        cameraFacing,
        toggleCamera,
        switchCamera,
        stopLocalVideo,
        localVideoTrack,

        // Refs
        // Refs
        activeRoomIdRef,
        livekitRoom,

        // Actions
        connectToLiveKit,
        handleCreateRoom,
        handleJoinRoom,
        handleLeaveRoom,
        handleEndRoom,
        toggleMute,
        requestToSpeak,
        lowerHand,
        toggleSpeakerHand,
        refreshRoomDetails,
        sendReaction,
        editRoomTitle,
        muteAll,
        toggleLock,
        toggleOpenMic,
        startRecording,
        stopRecording,
        stepDown,
        broadcastRoleChange,
        inviteToSpeak,
        acceptSpeakInvite,
        rejectSpeakInvite,
        revokeCoHost,
        speakerInvite,

        // Navigation
        navigate,
    };

    // Expose join for components that can't use the hook (e.g. PostNew.js)
    window.__audioRoomJoin = async (roomId) => {
        await handleJoinRoom(roomId);
        navigate('/audio-rooms');
    };

    // Same escape hatch for live video streams — join then open the stream page.
    window.__liveStreamJoin = async (roomId) => {
        await handleJoinRoom(roomId);
        navigate('/live-stream');
    };

    return (
        <AudioRoomContext.Provider value={value}>
            {children}
        </AudioRoomContext.Provider>
    );
};

export default AudioRoomContext;
